import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Col, Form, Grid, Row } from 'antd';
import type { FormAction } from '@/types/FormAction';
import { TableDefault } from '@/components/antd/TableDefault';
import { Container } from '@/components/Container';
import { Case, Match } from '@/components/util/Match';
import { ErrorComponent } from '@/features/erro/components/ErrorComponent';
import { FornecedorSelect } from '@/features/fornecedor/components/FornecedorSelect';
import { useLoginStore } from '@/features/login/store';
import { apiProdutoComprasQueryOptions } from '@/features/produto/api/apiProdutoCompras';
import { usePagination } from '@/std/pagination/usePagination';
import { columns } from './columns';

export function ProdutoTabFornecedor({
    action,
    proIdpk,
}: {
    action?: FormAction;
    proIdpk?: number;
}) {
    const navigate = useNavigate();
    const breakpoint = Grid.useBreakpoint();

    const isFintech = useLoginStore((s) => s.isFintech);

    const { params, tablePagination, onTableChange } = usePagination(undefined, {
        order: 'prf_compra_data desc',
    });

    const enabled = action !== 'cadastrar' && !!proIdpk && !isFintech;

    const { data, isFetching, error } = useQuery({
        ...apiProdutoComprasQueryOptions({ idpk: proIdpk, params }),
        enabled,
    });

    return (
        <div className='flex flex-col gap-4'>
            <Container>
                <Row gutter={[8, 8]}>
                    <Col lg={12} md={12} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            label='Fornecedor padrão'
                            name='pro_fornecedor_idpk'
                            tooltip={`Ao importar uma nota de compra, automaticamente este campo é atualizado com o fornecedor que realizou a última compra deste produto. \n
                                Este campo é amplamente utilizado para filtros nos relatórios de vendas e estoque.`}
                        >
                            <FornecedorSelect autoFocus={true} fetchOnMount={true} />
                        </Form.Item>
                    </Col>
                </Row>
            </Container>

            <Match fallback={null}>
                <Case when={!!error}>{error ? <ErrorComponent error={error} /> : null}</Case>

                <Case when={enabled}>
                    <Container title='Histórico de compras'>
                        <TableDefault
                            columns={columns}
                            dataSource={data?.registros}
                            loading={isFetching}
                            onChange={onTableChange}
                            onRow={(record) => ({
                                onClick: () => {
                                    if (record?.prf_compra_idpk) {
                                        void navigate({
                                            params: {
                                                action: 'visualizar',
                                                idpk: record.prf_compra_idpk,
                                            },
                                            to: '/a/compra/$action/{-$idpk}',
                                        });
                                    }
                                },
                            })}
                            pagination={tablePagination}
                            rowKey='prf_idpk'
                            scroll={{ x: !breakpoint.xxl ? 1600 : undefined }}
                        />
                    </Container>
                </Case>
            </Match>
        </div>
    );
}
